import React from 'react';
import { 
  X, 
  BookOpen, 
  Calendar, 
  Flag, 
  Layers, 
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './styles.scss';

import { downloadSyllabusSource } from '../../../services/academicService';

const CourseDetailModal = ({ isOpen, onClose, course, requirementTitle }) => {
  if (!isOpen) return null;

  const handleDownload = async () => {
    if (!course?.code) return;
    try {
      const blob = await downloadSyllabusSource(course.code);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Dynamically determine extension from sourceFilePath if available
      let extension = 'md';
      if (course.sourceFilePath && course.sourceFilePath.includes('.')) {
        extension = course.sourceFilePath.split('.').pop();
      }
      
      link.setAttribute('download', `${course.code.replace(/\s+/g, '_')}_Syllabus.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download source syllabus:', error);
      alert('Could not download official source document. Ensure the intelligence refresh has been completed.');
    }
  };

  const [docUrl, setDocUrl] = React.useState(null);
  const [docType, setDocType] = React.useState(null); // 'pdf' or 'docx'
  const [loadingDoc, setLoadingDoc] = React.useState(false);
  const [docError, setDocError] = React.useState(null);

  React.useEffect(() => {
    if (isOpen && course?.syllabus) {
      setLoadingDoc(true);
      setDocError(null);
      downloadSyllabusSource(course.code)
        .then((blob) => {
          const path = course.sourceFilePath || '';
          const isPdf = path.toLowerCase().endsWith('.pdf') || !path.toLowerCase().endsWith('.docx');
          
          const mimeType = isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          const fileBlob = new Blob([blob], { type: mimeType });
          const url = URL.createObjectURL(fileBlob);
          
          setDocUrl(url);
          setDocType(isPdf ? 'pdf' : 'docx');
          setLoadingDoc(false);
        })
        .catch((err) => {
          console.error("Failed to load official syllabus source document", err);
          setDocError("Original document cannot be resolved directly inside the frame. Please download using the action button below.");
          setLoadingDoc(false);
        });
    } else {
      setDocUrl(null);
      setDocType(null);
      setLoadingDoc(false);
      setDocError(null);
    }
    
    return () => {
      if (docUrl) {
        URL.revokeObjectURL(docUrl);
      }
    };
  }, [isOpen, course]);

  // Use course object if found, otherwise fallback to requirement info
  const title = course?.title || requirementTitle || 'Requirement Details';
  const code = course?.code || 'GENE CORE';
  const concentration = course?.concentration || 'General Education';
  
  // Robustly extract syllabus text with prioritized fallbacks
  const syllabusText = course?.syllabus?.rawText || 
                       course?.syllabus?.description || 
                       (course ? 'Full syllabus intelligence for this course is ingested but specific raw text is being parsed. Please reach out to university advisors for the detailed syllabus.' : 
                                 'This is a general education or transposed requirement. Detailed syllabus intelligence is available for core upper-division courses.');

  // Rich markdown rendering using industry-standard marked & dompurify
  const renderFormattedText = (text) => {
    if (!text) return null;

    // Securely parse complete markdown syntax with line breaks enabled so raw newlines are preserved
    const htmlContent = DOMPurify.sanitize(marked.parse(text, { breaks: true, gfm: true }));

    return (
      <div 
        className="markdown-body-content"
        dangerouslySetInnerHTML={{ __html: htmlContent }} 
      />
    );
  };

  return (
    <div className="modal-overlay-glass" onClick={onClose}>
      <div 
        className="course-detail-modal-premium" 
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <div className="header-content">
            <div className="icon-badge">
              <BookOpen size={24} />
            </div>
            <div className="title-stack">
              <span className="course-code-pill">{code}</span>
              <h2>{title}</h2>
            </div>
          </div>
          <button className="close-btn-round" onClick={onClose}>
            <X size={20} />
          </button>
        </header>

        <div className="modal-body-scrollable">
          <section className="metadata-grid">
            <div className="meta-item">
              <Layers size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Concentration</label>
                <p>{concentration}</p>
              </div>
            </div>
            <div className="meta-item">
              <Calendar size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Credits</label>
                <p>{course?.credits || 3.0} {course?.credits === 1 ? 'Credit Hour' : 'Credit Hours'}</p>
              </div>
            </div>
            <div className="meta-item">
              <Flag size={18} className="meta-icon" />
              <div className="meta-info">
                <label>Status</label>
                <p className={course?.syllabus ? 'status-active' : 'status-pending'}>
                  {course?.syllabus ? 'Syllabus Ingested' : 'Curriculum Metadata Only'}
                </p>
              </div>
            </div>
            {course?.transferDecision && (
              <div className="meta-item">
                <ChevronRight size={18} className="meta-icon icon-highlighted" />
                <div className="meta-info">
                  <label>Transfer Decision</label>
                  <p className="status-active">{course.transferDecision}</p>
                </div>
              </div>
            )}
          </section>

          <section className="syllabus-intelligence">
             <h3>
               <BookOpen size={18} />
               Official Syllabus Document
             </h3>
             <div className="rich-content-box">
                {course?.syllabus ? (
                  <>
                    {loadingDoc && (
                      <div className="document-loading-view">
                        <div className="spinner-ring"></div>
                        <p>Resolving official institutional document stream...</p>
                      </div>
                    )}

                    {docError && (
                      <div className="document-error-view">
                        <p>{docError}</p>
                      </div>
                    )}

                    {!loadingDoc && !docError && docUrl && docType === 'pdf' && (
                      <div className="embedded-pdf-container">
                        <iframe 
                          src={`${docUrl}#toolbar=0&navpanes=0&scrollbar=1`} 
                          title="Official Syllabus Document Viewer" 
                          width="100%" 
                          height="580px" 
                          className="embedded-pdf-iframe"
                        />
                      </div>
                    )}

                    {!loadingDoc && !docError && docUrl && docType === 'docx' && (
                      <div className="docx-preview-container">
                        <div className="docx-card">
                          <Flag className="docx-icon" size={32} />
                          <div className="docx-info">
                            <h4>Word Document (.docx) Syllabus</h4>
                            <p>This institutional syllabus is structured in MS Word format, which cannot be natively framed. You can download and read the original file immediately.</p>
                          </div>
                          <button className="btn-primary-gradient download-btn-sm" onClick={handleDownload}>
                            Download Original File
                          </button>
                        </div>
                        
                        <div className="docx-fallback-divider">
                          <span>OR BROWSE SEARCHABLE EXTRACT</span>
                        </div>
                        
                        <div className="raw-text-viewer">
                          {renderFormattedText(syllabusText)}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="raw-text-viewer">
                    {renderFormattedText(syllabusText)}
                  </div>
                )}
             </div>
          </section>
        </div>

        <footer className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Dismiss
          </button>
          <button 
            className={course?.syllabus ? "btn-primary-gradient" : "btn-disabled"} 
            onClick={handleDownload}
            disabled={!course?.syllabus}
            title={course?.syllabus ? "Download authentic institutional document" : "This requirement (GenEd/Transposed) does not have a digital syllabus ingested in the Hub."}
          >
            {course?.syllabus ? "Download Source Syllabus" : "Syllabus Not Ingested"}
            {course?.syllabus && <ChevronRight size={16} />}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default CourseDetailModal;
